import Typography from '@shared/constants/Typography';
import type {AssetDetail, MeterReadingEntry} from './CreateFlowProps';

export const CREATE_FLOW_TOTAL_STEPS = 5;

export const WORK_ORDER_STEP_IDS = {
  ASSET_ORGANIZATION_SELECTION: 1,
  ASSET_DETAILS_VIEW: 2,
  WORK_ORDER_FORM: 3,
  REPAIR_CREATION: 4,
  REPAIR_SELECTION: 5,
} as const;

export const CREATE_FLOW_NS = 'createFlow' as const;
export const ASSET_DETAILS_STEP_NS =
  'createFlow.workOrder.assetDetailsStep' as const;

export const _AVG_CHAR_FACTOR = 0.53;
export const _LABEL_FONT_SIZE = Typography.fontSize.fontSize16;

/** Priority levels for PM and Scheduled Action items */
export const PRIORITY_LEVELS = {
  URGENT: 'urgent',
  HIGH: 'high',
  LOW: 'low',
} as const;

/** Number of asset detail fields rendered per row */
export const ASSET_FIELDS_PER_ROW = 3;

/**
 * Open Work Orders table columns config.
 * `label`   — full i18n key for the column header
 * `dataKey` — property name on WorkOrderRow
 */
export const OPEN_WORK_ORDER_COLUMNS: Array<{
  label: string;
  dataKey: 'documentNumber' | 'dateIn' | 'dateOut' | 'status';
}> = [
  {
    label:
      'createFlow.workOrder.assetDetailsStep.openWorkOrders.documentNumber',
    dataKey: 'documentNumber',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.openWorkOrders.dateIn',
    dataKey: 'dateIn',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.openWorkOrders.dateOut',
    dataKey: 'dateOut',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.openWorkOrders.status',
    dataKey: 'status',
  },
];

/**
 * Replacement section fields config.
 * `label`   — full i18n key for the field label
 * `dataKey` — property name on ReplacementInfo
 */
export const REPLACEMENT_FIELDS: Array<{
  label: string;
  dataKey: 'totalPointValue' | 'originalReplacementDate' | 'pointScaleUsed';
}> = [
  {
    label: 'createFlow.workOrder.assetDetailsStep.replacement.totalPointValue',
    dataKey: 'totalPointValue',
  },
  {
    label:
      'createFlow.workOrder.assetDetailsStep.replacement.originalReplacementDate',
    dataKey: 'originalReplacementDate',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.replacement.pointScaleUsed',
    dataKey: 'pointScaleUsed',
  },
];

/**
 * Ordered i18n keys for the PM detail table column headers.
 * Used as the ListHeaderComponent in the PMDetailTable FlatList.
 */
export const PM_TABLE_HEADER_KEYS = [
  'createFlow.workOrder.assetDetailsStep.preventiveMaintenance.pmTypeDetails',
  'createFlow.workOrder.assetDetailsStep.preventiveMaintenance.cycleType',
  'createFlow.workOrder.assetDetailsStep.preventiveMaintenance.cycleLength',
  'createFlow.workOrder.assetDetailsStep.preventiveMaintenance.previousDueBegins',
  'createFlow.workOrder.assetDetailsStep.preventiveMaintenance.next',
  'createFlow.workOrder.assetDetailsStep.preventiveMaintenance.dueIn',
  'createFlow.workOrder.assetDetailsStep.preventiveMaintenance.errors',
] as const;

/**
 * Ordered i18n keys for the Scheduled Actions table column headers.
 * Used as the ListHeaderComponent in the ScheduledActionsSection FlatList.
 */
export const SA_TABLE_HEADER_KEYS = [
  'createFlow.workOrder.assetDetailsStep.scheduledActions.description',
  'createFlow.workOrder.assetDetailsStep.scheduledActions.cycleType',
  'createFlow.workOrder.assetDetailsStep.scheduledActions.cycleLength',
  'createFlow.workOrder.assetDetailsStep.scheduledActions.previousDueBegins',
  'createFlow.workOrder.assetDetailsStep.scheduledActions.next',
  'createFlow.workOrder.assetDetailsStep.scheduledActions.dueIn',
] as const;

/**
 * Contact section filter tabs config.
 * `value`    — the tab identifier (maps to ContactType | 'all')
 * `labelKey` — full i18n key for the tab label
 * `a11yKey`  — full i18n key for the accessibility label
 */
export const CONTACT_TABS: Array<{
  value: 'current' | 'past' | 'all';
  labelKey: string;
  a11yKey: string;
}> = [
  {
    value: 'current',
    labelKey: 'createFlow.workOrder.assetDetailsStep.contacts.tabCurrent',
    a11yKey: 'createFlow.workOrder.accessibility.contactTabCurrent',
  },
  {
    value: 'past',
    labelKey: 'createFlow.workOrder.assetDetailsStep.contacts.tabPast',
    a11yKey: 'createFlow.workOrder.accessibility.contactTabPast',
  },
  {
    value: 'all',
    labelKey: 'createFlow.workOrder.assetDetailsStep.contacts.tabAll',
    a11yKey: 'createFlow.workOrder.accessibility.contactTabAll',
  },
];

/**
 * Asset detail fields config.
 * `label`   — full i18n key passed to t()
 * `dataKey` — property name on the AssetDetail response object
 */
export const ASSET_DETAIL_FIELDS: Array<{
  label: string;
  dataKey: keyof Omit<AssetDetail, 'imageUrl'>;
}> = [
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.assetNumber',
    dataKey: 'assetNumber',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.organization',
    dataKey: 'organization',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.status',
    dataKey: 'status',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.year',
    dataKey: 'year',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.make',
    dataKey: 'make',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.model',
    dataKey: 'model',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.license',
    dataKey: 'license',
  },
  {
    label: 'createFlow.workOrder.assetDetailsStep.assetDetails.vinSerialNumber',
    dataKey: 'vinSerialNumber',
  },
];
// ─── WorkOrderFormStepThree constants ─────────────────────────────────────────

/**
 * Named keys for each column in the Meter Information table.
 * Use these instead of raw string literals in switch-cases and comparisons.
 */
export const METER_COL_KEYS = {
  METER_TYPE:     'meterType',
  READING:        'reading',
  ACTUAL_READING: 'actualReading',
  ERROR:          'error',
  ACTION:         'action',
} as const;

export type MeterColKey = typeof METER_COL_KEYS[keyof typeof METER_COL_KEYS];

/**
 * Meter Information table column config.
 * `key`   — field key on MeterReadingEntry (or 'action' for the dropdown)
 * `label` — full i18n key for the column header
 */
export const METER_TABLE_COLUMNS: Array<{
  key: MeterColKey;
  label: string;
  isMandatory?: boolean;
  flex: number;
  width: number;
}> = [
  {
    key: METER_COL_KEYS.METER_TYPE,
    label: 'createFlow.workOrderFormStep.meterType',
    flex: 1,
    width: Typography.width.width140,
  },
  {
    key: METER_COL_KEYS.READING,
    label: 'createFlow.workOrderFormStep.reading',
    flex: 1,
    width: Typography.width.width154,
    isMandatory: true,
  },
  {
    key: METER_COL_KEYS.ACTUAL_READING,
    label: 'createFlow.workOrderFormStep.actualReading',
    flex: 1,
    width: Typography.width.width300,
  },
  {
    key: METER_COL_KEYS.ERROR,
    label: 'createFlow.workOrderFormStep.error',
    flex: 2,
    width: Typography.width.width300,
  },
  {
    key: METER_COL_KEYS.ACTION,
    label: 'createFlow.workOrderFormStep.action',
    flex: 1,
    width: Typography.width.width198,
  },
];

/** Initial placeholder meter rows shown in the Meter Information table. */
export const INITIAL_METER_ROWS: MeterReadingEntry[] = [
  {
    id: 'm1',
    meterType: 'M',
    reading: '',
    actualReading: '',
    error: '',
    actionValue: '',
    availableOptions: [],
    isRequiredWorkOrder: false,
  },
];

export const REPAIR_FLOW_NS = 'createFlow.repairFlow' as const;

export const REPAIR_FLOW_SHOP_ALL_VALUE = '' as const;

/** secureStorage key for the persisted repair flow step (survives page reload). */
export const REPAIR_FLOW_PERSIST_STEP_KEY = 'repair_flow_step' as const;

/** secureStorage key for the persisted selected work order JSON (survives page reload). */
export const REPAIR_FLOW_PERSIST_WORK_ORDER_KEY = 'repair_flow_work_order' as const;

// ─── Work Order Flow persistence keys ────────────────────────────────────────

/** secureStorage key for the persisted WO flow step (survives page reload). */
export const WO_FLOW_PERSIST_STEP_KEY = 'wo_flow_step' as const;

/** secureStorage key for the persisted validated asset JSON (survives page reload). */
export const WO_FLOW_PERSIST_ASSET_KEY = 'wo_flow_validated_asset' as const;

/** secureStorage key for the persisted WO document ID (survives page reload). */
export const WO_FLOW_PERSIST_DOCUMENT_ID_KEY = 'wo_flow_document_id' as const;

/** secureStorage key for the persisted step-1 state JSON (survives page reload). */
export const WO_FLOW_PERSIST_STEP1_KEY = 'wo_flow_step1' as const;

/**
 * Step identifiers for the Repair Flow.
 * Extend as additional steps are added.
 */
export const REPAIR_FLOW_STEP_IDS = {
  SELECT_WORK_ORDER: 1,
  CREATE_REPAIR: 2,
} as const;

export type RepairFlowStepId =
  (typeof REPAIR_FLOW_STEP_IDS)[keyof typeof REPAIR_FLOW_STEP_IDS];

/**
 * Column config for the Select Work Order list-view table.
 * Matches the same shape as the dashboard `columns` array so it can be
 * rendered with the same WorkOrdersListView-style header/row pattern.
 *
 * `key`            — property name on RepairFlowWorkOrder (or 'action' for the checkbox column)
 * `label`          — full i18n key for the column header
 * `width`          — fixed pixel width using Typography.width constants
 * `itemCenterAlign`— (optional) centre-align header and cell content
 */
export const REPAIR_FLOW_LIST_COLUMNS: Array<{
  key: string;
  label: string;
  width: number;
  flex: number;
  itemCenterAlign?: boolean;
  sortKey?: string;
}> = [
  {
    key: 'action',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.action',
    width: Typography.width.width60,
    flex: 0.6,
    itemCenterAlign: true,
  },
  {
    key: 'assetNumber',
    sortKey: 'assetNumber',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.asset',
    width: Typography.width.width110,
    flex: 1,
  },
  {
    key: 'woNumber',
    sortKey: 'documentNumber',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.woNumber',
    width: Typography.width.width140,
    flex: 1.2,
  },
  {
    key: 'woStatus',
    sortKey: 'status',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.woStatus',
    width: Typography.width.width140,
    flex: 1.2,
  },
  {
    key: 'description',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.description',
    width: Typography.width.width200,
    flex: 2,
  },
  {
    key: 'licenseNumber',
    sortKey: 'license',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.licenseNumber',
    width: Typography.width.width160,
    flex: 1.4,
  },
  {
    key: 'shop',
    sortKey: 'maintShop',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.shop',
    width: Typography.width.width110,
    flex: 0.8,
  },
  {
    key: 'dateCreated',
    sortKey: 'dateTimeIn',
    label: 'createFlow.repairFlow.selectWorkOrder.listView.dateCreated',
    width: Typography.width.width160,
    flex: 1.4,
  },
];

/** Default number of warranty rows shown per page. */
export const WARRANTY_DEFAULT_ROWS_PER_PAGE = 9;

/**
 * Column config for the first (header) row of each warranty item.
 * `labelKey` — sub-key within createFlow.workOrder.assetDetailsStep.warranties
 * `dataKey`  — property name on WarrantyItem
 */
export const WARRANTY_ROW1_COLUMNS: ReadonlyArray<{
  labelKey: string;
  dataKey: 'warrantyCategory' | 'warrantyType' | 'description';
  flex: number;
}> = [
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.warrantyCategory',
    dataKey: 'warrantyCategory',
    flex: 1,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.warrantyType',
    dataKey: 'warrantyType',
    flex: 1,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.description',
    dataKey: 'description',
    flex: 2,
  },
];

/**
 * Column config for the second (detail) row of each warranty item.
 * `labelKey` — sub-key within createFlow.workOrder.assetDetailsStep.warranties
 * `dataKey`  — property name on WarrantyItem
 */
export const WARRANTY_ROW2_COLUMNS: ReadonlyArray<{
  labelKey: string;
  dataKey: 'deductible' | 'cost' | 'vendor' | 'referenceNumber';
  flex: number;
}> = [
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.deductible',
    dataKey: 'deductible',
    flex: 1,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.cost',
    dataKey: 'cost',
    flex: 1,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.vendor',
    dataKey: 'vendor',
    flex: 2,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.referenceNumber',
    dataKey: 'referenceNumber',
    flex: 1,
  },
];

/**
 * Column config for the expanded cycle detail table within each warranty item.
 * `labelKey` — full i18n key for the column header
 * `dataKey`  — property name on WarrantyCycle
 */
export const WARRANTY_CYCLE_COLUMNS: ReadonlyArray<{
  labelKey: string;
  dataKey: 'cycleType' | 'cycleLength' | 'begins' | 'expires';
  flex: number;
  width: number;
  itemCenterAlign?: boolean;
}> = [
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.cycleType',
    dataKey: 'cycleType',
    flex: 1,
    width: Typography.width.width160,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.cycleLength',
    dataKey: 'cycleLength',
    flex: 1,
    width: Typography.width.width160,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.begins',
    dataKey: 'begins',
    flex: 1,
    width: Typography.width.width160,
  },
  {
    labelKey: 'createFlow.workOrder.assetDetailsStep.warranties.expires',
    dataKey: 'expires',
    flex: 1,
    width: Typography.width.width160,
  },
];

export const MAX_SYMPTOM_LENGTH = 125